import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage, WidgetComponent, getRegistration } from '@chewy/chat-library-common';
import MessageStamp from './MessageStamp';
import { KibInlineAlert } from "@chewy/kib-messaging-react";
import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css';
import '@chewy/kib-messaging-styles/dist/styles/kib-inline-alert.css';
import styles from "./AiChatMessage.module.scss";

export interface AiChatMessageProps {
  message: ChatMessage;
  handleScroll?: (element: HTMLDivElement) => void;
}

async function renderMessage<T extends ChatMessage>(
  message: T,
): Promise<React.ReactNode | null> {
  try {
    const registration = getRegistration(message.type);

    if (!registration?.component) {
      console.warn(`No component registered for message type: ${message.type}`);
      return null;
    }

    // Cast to WidgetComponent since we're passing message-based props
    const Component = registration.component as WidgetComponent<any>;
    return React.createElement(Component, { message });
  } catch (error) {
    console.error(error);
    return null;
  }
}

export default function AiChatMessage({ message, handleScroll }: AiChatMessageProps) {
  const [renderedContent, setRenderedContent] = useState<React.ReactNode | null | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const isHumanMessage = message.responseType === 'user' || message.responseType === 'agent';
  const shouldShowStamp = isHumanMessage;

  useEffect(() => {
    const loadMessage = async () => {
      const content = await renderMessage(message);
      setRenderedContent(content ?? null);
    };

    loadMessage();
  }, [message]);

  useEffect(() => {
    if (handleScroll && renderedContent && containerRef.current) {
      handleScroll(containerRef.current);
    };
  }, [handleScroll, message.id, renderedContent]);

  return (
    <div ref={containerRef} data-testid={`chat-message-${message.responseType}`}>
      {!!renderedContent && (
        <>
          {shouldShowStamp && message.responseTimestamp && (
            <MessageStamp message={message} />
          )}
          {renderedContent}
        </>
      )}
      {message.error && <div className={styles.errors}>
        <KibInlineAlert severity="error">
          <span className={styles.alertContent}>{message.error}</span>
        </KibInlineAlert>
      </div>}
    </div>
  );
}
